import { useRef, useState } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, Pt, RoundProps, ViewProps } from '../kit.js';
import { BAD, BoardFrame, INK, OK, TYPE, WARN, clamp, easier, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import type { Status } from '../kit.js';
import { ERA_SKIN } from '../../art.js';

/**
 * ENGINEER · SWAP THE FUSES — tap each fuse that starts to glow, before it blows.
 *
 * A fuse box of glass cartridge fuses in their clips. One at a time a fuse starts to heat: its
 * wire warms smoothly from grey through amber to white over two seconds or more (a ramp, never a
 * blink), with a warm halo and a shimmer above it. Tap it and it is swapped for a fresh one.
 * Letting it reach full heat blows it (smoke, a blackened glass) and tapping a cold fuse is a
 * mistake too. A fuse just swapped, or blown and being replaced, ignores taps, so a double tap is
 * never a slip. The status line counts the swaps: FUSES 2 / 5.
 *
 * Round 1: six fuses, a slow warm-up, a calm pause between them, five to swap. Round 2: six
 * fuses, a little quicker, five to swap. Round 3: eight fuses, quicker still and shorter pauses,
 * six to swap. Only one glows at a time. (A second fuse glowing while the first still does,
 * `pairOdds`, is still in the rules; no round deals it since 6 Oct 2026.)
 *
 * Converted to the kit (kit.tsx): the rules as plain functions, a small view state (`FusesView`),
 * a pure `View`, the `Board` that plays it on the game clock, and the `demo` the how-to plays.
 */

export interface FuseLevel {
  cols: number;
  rows: number;
  /** From the first warmth to the fuse blowing, in ms. */
  warmMs: number;
  /** The pause after a fuse is dealt with before the next starts to glow: the least and the most. */
  restMs: readonly [number, number];
  /** How likely a second fuse is to start glowing soon after the first. */
  pairOdds: number;
  /** How soon after the first the second starts: the least and the most. */
  pairMs: readonly [number, number];
  /** Fuses to swap to clear the round. */
  need: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const FUSE_LEVELS: readonly FuseLevel[] = easier(
  [
    { cols: 3, rows: 2, warmMs: 2_600, restMs: [800, 1_200], pairOdds: 0, pairMs: [0, 0], need: 5 },
    { cols: 4, rows: 2, warmMs: 2_100, restMs: [600, 1_000], pairOdds: 0, pairMs: [0, 0], need: 6 },
  ],
);

/** A blown fuse stands this long before it is replaced; a fresh one ignores taps this long. */
export const BLOWN_MS = 1_100;
export const FRESH_MS = 650;

export type FuseState =
  | { phase: 'cold' }
  | { phase: 'hot'; since: number }
  | { phase: 'blown'; since: number }
  | { phase: 'fresh'; since: number };

/** How hot a glowing fuse is, 0 to 1 (1 blows it). */
export const heatOf = (since: number, now: number, warmMs: number) => Math.max(0, Math.min(1, (now - since) / warmMs));

export type FuseVerdict = 'swap' | 'ignore' | 'mistake';

/** What a tap on a fuse does: swaps a glowing one, ignores one just swapped or blown, else a mistake. */
export function judgeFuse(state: FuseState): FuseVerdict {
  if (state.phase === 'hot') return 'swap';
  if (state.phase === 'cold') return 'mistake';
  return 'ignore';
}

/** A cold fuse to heat next, away from `avoid` when there is a choice; -1 if none is cold. */
export function pickCold(states: readonly FuseState[], rand: () => number, avoid: readonly number[] = []): number {
  const cold = states.map((s, i) => (s.phase === 'cold' ? i : -1)).filter((i) => i >= 0);
  if (!cold.length) return -1;
  const away = cold.filter((i) => !avoid.includes(i));
  const pool = away.length ? away : cold;
  return pool[Math.floor(rand() * pool.length)]!;
}

/** The fuse box as the round plays it. */
export interface FuseBox {
  fuses: FuseState[];
  swaps: number;
  /** When the next fuse starts to glow (null while one glows). */
  nextAt: number | null;
  /** When a second fuse joins the first, on the rounds that do that. */
  pairAt: number | null;
  /** The last two fuses to glow, so the next is somewhere else. */
  last: number[];
}

/** Before the first fuse of a round starts to glow. */
export const FIRST_MS = 800;

export function newBox(lv: FuseLevel, now: number): FuseBox {
  return {
    fuses: Array.from({ length: lv.cols * lv.rows }, () => ({ phase: 'cold' }) as FuseState),
    swaps: 0,
    nextAt: now + FIRST_MS,
    pairAt: null,
    last: [],
  };
}

/**
 * The box moves on to `now`: a fuse at full heat blows, a blown one is replaced, a fresh one
 * settles, and the next starts to glow after a pause. Returns how many blew.
 */
export function stepBox(box: FuseBox, lv: FuseLevel, now: number, rand: () => number): number {
  const all = box.fuses;
  let blew = 0;
  all.forEach((f, i) => {
    if (f.phase === 'hot' && heatOf(f.since, now, lv.warmMs) >= 1) {
      all[i] = { phase: 'blown', since: now };
      blew += 1;
    } else if (f.phase === 'blown' && now - f.since >= BLOWN_MS) all[i] = { phase: 'fresh', since: now };
    else if (f.phase === 'fresh' && now - f.since >= FRESH_MS) all[i] = { phase: 'cold' };
  });
  if (box.swaps >= lv.need) return blew;
  const hot = all.filter((f) => f.phase === 'hot').length;
  const wanted = lv.need - box.swaps;
  const heat = () => {
    const i = pickCold(all, rand, box.last);
    if (i < 0) return false;
    all[i] = { phase: 'hot', since: now };
    box.last = [...box.last, i].slice(-2);
    return true;
  };
  if (box.pairAt !== null && now >= box.pairAt) {
    box.pairAt = null;
    if (hot < wanted) heat();
  }
  if (box.nextAt !== null && now >= box.nextAt && hot === 0 && heat()) {
    box.nextAt = null;
    if (wanted > 1 && rand() < lv.pairOdds) box.pairAt = now + lv.pairMs[0] + (lv.pairMs[1] - lv.pairMs[0]) * rand();
  }
  // Nothing glowing and nothing on its way: the next one after a pause.
  if (box.nextAt === null && box.pairAt === null && all.every((f) => f.phase !== 'hot')) {
    box.nextAt = now + lv.restMs[0] + (lv.restMs[1] - lv.restMs[0]) * rand();
  }
  return blew;
}

/** A tap on fuse `i` at `now`: swaps it if it glows (the round's last swap cools the rest). */
export function tapBox(box: FuseBox, lv: FuseLevel, i: number, now: number): FuseVerdict {
  const v = judgeFuse(box.fuses[i]!);
  if (v !== 'swap') return v;
  box.fuses[i] = { phase: 'fresh', since: now };
  box.swaps += 1;
  if (box.swaps >= lv.need) box.fuses = box.fuses.map((f) => (f.phase === 'hot' ? { phase: 'cold' } : f));
  return v;
}

const rgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
const blend = (a: string, b: string, t: number) => {
  const [r1, g1, b1] = rgb(a);
  const [r2, g2, b2] = rgb(b);
  const k = clamp(t, 0, 1);
  return `rgb(${Math.round(r1 + (r2 - r1) * k)},${Math.round(g1 + (g2 - g1) * k)},${Math.round(b1 + (b2 - b1) * k)})`;
};
/** The wire's colour as it heats: grey, then amber (the kit's careful), then nearly white. */
export const wireColour = (heat: number) => (heat < 0.55 ? blend(INK.faint, WARN, heat / 0.55) : blend(WARN, INK.hi, (heat - 0.55) / 0.45));

// -- the view -------------------------------------------------------------------------------------

/** A fuse's phase, by its number in a view state. */
export const FUSE_PHASE = { cold: 0, hot: 1, blown: 2, fresh: 3 } as const;
/** What the status line says after a slip. */
export const FUSE_NOTE = { none: 0, cold: 1, blew: 2 } as const;
type Note = (typeof FUSE_NOTE)[keyof typeof FUSE_NOTE];

/** Everything the fuse box shows. */
export interface FusesView {
  /** The game clock, ms. */
  t: number;
  /** The box: columns and rows of fuses. */
  c: number;
  r: number;
  /** Each fuse's phase (FUSE_PHASE), and with it: a hot one's heat (0–1), ms since a blown or fresh one became so, 0 for a cold one. */
  ph: readonly number[];
  v: readonly number[];
  /** Fuses swapped, and how many to swap. */
  n: number;
  of: number;
  /** The last slip (FUSE_NOTE), while it shows. */
  note: Note;
  /** 0 playing, 1 all swapped. */
  s: 0 | 1;
  fx?: Fx | undefined;
}

/** The panel of fuses on the board. */
const PANEL = { x: 18, y: 36, w: 284, h: 270 } as const;
/** A slip's words show this long. */
const NOTE_MS = 1_200;

/** The fuse under a finger at `p`, or −1. */
export function fuseAt(cols: number, rows: number, p: Pt): number {
  const c = Math.floor(((p.x - PANEL.x) * cols) / PANEL.w);
  const r = Math.floor(((p.y - PANEL.y) * rows) / PANEL.h);
  return c < 0 || r < 0 || c >= cols || r >= rows ? -1 : r * cols + c;
}

/** The fuse box, from its view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<FusesView>) {
  const skin = ERA_SKIN[era];
  const id = useSvgId();
  const { c: cols, r: rows, t } = view;
  const cw = PANEL.w / cols;
  const ch = PANEL.h / rows;
  const gw = Math.min(24, cw * 0.28);
  const gl = Math.min(ch * 0.5, 64);
  // A demo glides every number between its keys: counts and phases are read rounded.
  const note = Math.round(view.note) as Note;
  const status: Status =
    Math.round(view.s) === 1
      ? { text: say(lang, '✓ ALL SWAPPED', '✓ ALT BYTTET'), tone: 'ok' }
      : note === FUSE_NOTE.cold
        ? { text: say(lang, 'THAT ONE WAS COLD', 'DEN VAR KALD'), tone: 'bad' }
        : note === FUSE_NOTE.blew
          ? { text: say(lang, 'ONE BLEW — WATCH THE GLOW', 'EN GIKK – FØLG MED PÅ GLØDEN'), tone: 'bad' }
          : { text: say(lang, 'FUSES', 'SIKRINGER'), count: [Math.round(view.n), view.of], hint: say(lang, 'TAP THE ONE THAT GLOWS', 'TRYKK PÅ DEN SOM GLØDER') };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}frame`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#56616c" />
          <stop offset=".5" stopColor="#2a3139" />
          <stop offset="1" stopColor="#12171c" />
        </linearGradient>
        <linearGradient id={`${id}base`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2a2f35" />
          <stop offset="1" stopColor="#15191d" />
        </linearGradient>
        <linearGradient id={`${id}cap`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#6d5a36" />
          <stop offset=".4" stopColor="#f3dca2" />
          <stop offset="1" stopColor="#5a4a2c" />
        </linearGradient>
        <linearGradient id={`${id}clip`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#3b434b" />
          <stop offset=".45" stopColor="#d5dde4" />
          <stop offset="1" stopColor="#333a41" />
        </linearGradient>
        <linearGradient id={`${id}glass`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={INK.hi} stopOpacity=".22" />
          <stop offset=".3" stopColor={INK.hi} stopOpacity=".04" />
          <stop offset=".75" stopColor={INK.hi} stopOpacity=".02" />
          <stop offset="1" stopColor={INK.hi} stopOpacity=".14" />
        </linearGradient>
        <radialGradient id={`${id}halo`}>
          <stop offset="0" stopColor={WARN} stopOpacity=".85" />
          <stop offset=".55" stopColor={WARN} stopOpacity=".3" />
          <stop offset="1" stopColor={WARN} stopOpacity="0" />
        </radialGradient>
        <pattern id={`${id}hazard`} width="14" height="14" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="7" height="14" fill={WARN} />
          <rect x="7" width="7" height="14" fill="#15191d" />
        </pattern>
      </defs>

      {/* the fuse box: its frame, a hazard strip, the panel */}
      <rect x="6" y="6" width="308" height="308" rx="10" fill={`url(#${id}frame)`} />
      <rect x="12" y="12" width="296" height="14" rx="3" fill={`url(#${id}hazard)`} opacity=".7" />
      <rect x={PANEL.x - 6} y={PANEL.y - 4} width={PANEL.w + 12} height={PANEL.h + 8} rx="6" fill="#0a0d11" stroke={skin.ink} strokeOpacity=".25" />

      {view.ph.map((phase, i) => {
        const ph = Math.round(phase);
        const v = view.v[i]!;
        const cx = PANEL.x + (i % cols) * cw + cw / 2;
        const cy = PANEL.y + Math.floor(i / cols) * ch + ch / 2;
        const heat = ph === FUSE_PHASE.hot ? clamp(v, 0, 1) : 0;
        // The glow comes up over the first part of the warm-up, then with the heat: a ramp, not a blink.
        const glow = ph === FUSE_PHASE.hot ? Math.min(1, heat * 6) * 0.3 + 0.7 * heat : 0;
        const blown = ph === FUSE_PHASE.blown;
        const freshK = ph === FUSE_PHASE.fresh ? Math.min(1, v / 350) : 1;
        const slide = ph === FUSE_PHASE.fresh ? -16 * (1 - freshK) * (1 - freshK) : 0;
        const top = cy - gl / 2;
        const bottom = cy + gl / 2;
        const zig = Array.from({ length: 7 }, (_, k) => `${k === 0 ? 'M' : 'L'}${(cx + (k % 2 ? 3.2 : -3.2)).toFixed(1)} ${(top + 5 + (k * (gl - 10)) / 6).toFixed(1)}`).join(' ');
        return (
          <g key={i}>
            <rect x={cx - cw / 2 + 4} y={cy - ch / 2 + 4} width={cw - 8} height={ch - 8} rx="7" fill={`url(#${id}base)`} stroke={INK.hi} strokeOpacity=".06" />
            <text x={cx - cw / 2 + 11} y={cy + ch / 2 - 12} fontSize={TYPE.hint} fontFamily={mono} fill={blown ? BAD : INK.dim} opacity=".85">
              F{i + 1}
            </text>
            {glow > 0 && <ellipse cx={cx} cy={cy} rx={cw * 0.46} ry={ch * 0.42} fill={`url(#${id}halo)`} opacity={glow} />}
            {/* the clips that hold it */}
            <rect x={cx - gw / 2 - 5} y={top - 7} width={gw + 10} height="9" rx="2" fill={`url(#${id}clip)`} />
            <rect x={cx - gw / 2 - 5} y={bottom - 2} width={gw + 10} height="9" rx="2" fill={`url(#${id}clip)`} />
            <g transform={`translate(0 ${slide.toFixed(2)})`} opacity={ph === FUSE_PHASE.fresh ? 0.35 + 0.65 * freshK : 1}>
              {/* the glass, tinted as it heats or smoked once blown */}
              <rect x={cx - gw / 2} y={top} width={gw} height={gl} rx="3" fill={blown ? '#2a1a12' : '#0c1116'} />
              {heat > 0 && <rect x={cx - gw / 2} y={top} width={gw} height={gl} rx="3" fill={WARN} opacity={heat * 0.3} />}
              {blown ? (
                <>
                  <path d={`M${cx} ${top + 4} L${cx - 2} ${cy - 6}`} stroke="#3a2a22" strokeWidth="1.6" />
                  <path d={`M${cx} ${bottom - 4} L${cx + 2} ${cy + 5}`} stroke="#3a2a22" strokeWidth="1.6" />
                  <ellipse cx={cx} cy={cy} rx={gw * 0.4} ry={gl * 0.2} fill="#000000" opacity=".5" />
                </>
              ) : (
                <path d={zig} stroke={wireColour(heat)} strokeWidth={1.4 + heat * 0.8} fill="none" strokeLinejoin="round" />
              )}
              <rect x={cx - gw / 2} y={top} width={gw} height={gl} rx="3" fill={`url(#${id}glass)`} stroke={INK.text} strokeOpacity=".35" strokeWidth=".8" />
              {/* its brass caps */}
              <rect x={cx - gw / 2 - 2} y={top - 3} width={gw + 4} height="9" rx="2" fill={`url(#${id}cap)`} />
              <rect x={cx - gw / 2 - 2} y={bottom - 6} width={gw + 4} height="9" rx="2" fill={`url(#${id}cap)`} />
            </g>
            {/* heat shimmer rising off a hot fuse, and smoke off a blown one: motion, not light */}
            {heat > 0.3 &&
              [0, 1].map((k) => {
                const u = (((t / 900 + k * 0.5) % 1) + 1) % 1;
                const y = top - 8 - u * 16;
                return <path key={k} d={`M${cx - 6 + k * 6} ${y} q3 -4 0 -8 q-3 -4 0 -8`} stroke={WARN} strokeWidth="1.2" fill="none" opacity={(heat - 0.3) * (1 - u)} />;
              })}
            {blown &&
              [0, 1, 2].map((k) => {
                const u = Math.min(1, v / BLOWN_MS - k * 0.12);
                if (u <= 0) return null;
                return <circle key={k} cx={cx + (k - 1) * 5} cy={top - 4 - u * 26} r={4 + u * 9} fill={INK.dim} opacity={0.45 * (1 - u)} />;
              })}
            {ph === FUSE_PHASE.fresh && (
              <text x={cx + gw / 2 + 8} y={top + 12} fontSize={TYPE.label + 2} fontWeight="700" fontFamily={mono} fill={OK} opacity={1 - Math.min(1, v / FRESH_MS)}>
                ✓
              </text>
            )}
          </g>
        );
      })}
    </BoardFrame>
  );
}

// -- the board -----------------------------------------------------------------------------------

function Board(props: RoundProps) {
  const { level, lang } = props;
  const lv = FUSE_LEVELS[level]!;
  const [rand] = useState(() => seeded(Date.now() + level * 9_973));
  const box = useRef<FuseBox | null>(null);
  const note = useRef<{ kind: Note; at: number }>({ kind: FUSE_NOTE.none, at: -1e9 });

  const round = useRound(props, (_dt, now, won) => {
    if (box.current === null) box.current = newBox(lv, now);
    const blew = stepBox(box.current, lv, now, rand);
    if (won || blew === 0) return;
    note.current = { kind: FUSE_NOTE.blew, at: now };
    for (let k = 0; k < blew; k++) round.miss();
  });
  const now = round.now;
  if (box.current === null) box.current = newBox(lv, now);

  const tap = (p: Pt) => {
    const b = box.current;
    if (!round.live || !b) return;
    const i = fuseAt(lv.cols, lv.rows, p);
    if (i < 0) return;
    const at = round.time();
    const v = tapBox(b, lv, i, at);
    if (v === 'ignore') return;
    if (v === 'mistake') {
      note.current = { kind: FUSE_NOTE.cold, at };
      round.miss();
      return;
    }
    note.current = { kind: FUSE_NOTE.none, at };
    round.hit();
    round.progress(b.swaps / lv.need);
    if (b.swaps >= lv.need) round.win();
  };

  const fuses = box.current.fuses;
  const view: FusesView = {
    t: q(now),
    c: lv.cols,
    r: lv.rows,
    ph: fuses.map((f) => FUSE_PHASE[f.phase]),
    v: fuses.map((f) => (f.phase === 'hot' ? q(heatOf(f.since, now, lv.warmMs), 2) : f.phase === 'cold' ? 0 : q(now - f.since))),
    n: box.current.swaps,
    of: lv.need,
    note: now - note.current.at < NOTE_MS ? note.current.kind : FUSE_NOTE.none,
    s: round.won ? 1 : 0,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Fuse box: tap each fuse that starts to glow', 'Sikringsskap: trykk på hver sikring som begynner å gløde'), down: tap }}
    />
  );
}

// -- the how-to ----------------------------------------------------------------------------------

/** The how-to's three fuses: which, when it starts to glow, and when the finger swaps it. */
const DEMO_FUSES = [
  [2, 400, 2_000],
  [3, 2_700, 4_300],
  [1, 4_900, 6_300],
] as const;

/**
 * The how-to, by round 1's own timing: a fuse warms and the finger swaps it while it is still only
 * amber; then another, somewhere else; then a third. Keyed every tenth of a second, with each change
 * of a fuse's phase keyed on its own moment.
 */
function demoFuses(): DemoScript<FusesView> {
  const lv = FUSE_LEVELS[0]!;
  const ms = 7_200;
  const count = lv.cols * lv.rows;
  const at = (t: number): Partial<FusesView> => {
    const ph: number[] = Array.from({ length: count }, () => FUSE_PHASE.cold);
    const v: number[] = Array.from({ length: count }, () => 0);
    let last = -1e9;
    for (const [i, warm, swap] of DEMO_FUSES) {
      if (t > warm && t <= swap) {
        ph[i] = FUSE_PHASE.hot;
        v[i] = q(heatOf(warm, t, lv.warmMs), 3);
      } else if (t > swap && t <= swap + FRESH_MS) {
        ph[i] = FUSE_PHASE.fresh;
        v[i] = q(t - swap);
      }
      if (t > swap) last = swap;
    }
    return {
      t: 0,
      c: lv.cols,
      r: lv.rows,
      ph,
      v,
      n: DEMO_FUSES.filter(([, , swap]) => t > swap).length,
      of: lv.need,
      note: FUSE_NOTE.none,
      s: 0,
      fx: [q(clamp(1 - (t - last) / 450, 0, 1), 2), 0],
    };
  };
  const changes = DEMO_FUSES.flatMap(([, warm, swap]) => [warm, swap, swap + FRESH_MS]);
  const keys: DemoKey<FusesView>[] = [];
  for (let t = 0; t <= ms; t += 100) keys.push({ at: t, view: at(t) });
  for (const c of changes) {
    if (c % 100 !== 0) keys.push({ at: c, view: at(c) });
    keys.push({ at: c + 10, view: at(c + 10), ease: 'step' });
  }
  keys.sort((a, b) => a.at - b.at);
  const centre = (i: number) => {
    const cw = PANEL.w / lv.cols;
    const ch = PANEL.h / lv.rows;
    return { x: q(PANEL.x + (i % lv.cols) * cw + cw / 2), y: q(PANEL.y + Math.floor(i / lv.cols) * ch + ch / 2) };
  };
  const finger: FingerKey[] = [
    { at: 0, x: 262, y: 304, hide: true },
    { at: 300, x: 262, y: 304, hide: false },
  ];
  for (const [i, , swap] of DEMO_FUSES) {
    const p = centre(i);
    finger.push({ at: swap - 500, ...p }, { at: swap, ...p, down: true }, { at: swap + 150, ...p, down: false });
  }
  finger.push({ at: 6_800, x: 262, y: 304, hide: true });
  return {
    ms,
    keys,
    finger,
    steps: [
      { at: 0, en: 'ONE FUSE STARTS TO GLOW', no: 'EN SIKRING BEGYNNER Å GLØDE' },
      { at: 1_400, en: 'TAP IT BEFORE IT BLOWS', no: 'TRYKK FØR DEN GÅR' },
      { at: 2_700, en: 'THEN THE NEXT ONE', no: 'SÅ DEN NESTE' },
    ],
  };
}

export const game = thirtyGame<FusesView>({
  Board,
  View,
  demo: demoFuses(),
  words: {
    en: {
      instruction: 'Tap each fuse as it starts to glow, before it blows. Leave the cold ones alone.',
      howMove: 'Watch the fuse box. Tap a fuse as soon as it starts to glow.',
    },
    no: {
      instruction: 'Trykk på sikringen som begynner å gløde, før den går. La de kalde være.',
      howMove: 'Følg med på sikringsskapet. Trykk på en sikring med en gang den gløder.',
    },
  },
});
